import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Breadcrumbs,
  Link as MUILink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TablePagination,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Modal, message } from "antd";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import PageTitle from "../../Components/PageTitle";
import axios from "../../axiosInstance";

const AllStudentList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [editFormData, setEditFormData] = useState({
    studentName: "",
    class: "",
    dob: "",
    parentName: "",
    phone: "",
    email: "",
    address: "",
  });

  // Fetch all students
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/v1/student/all");
      if (response?.data?.success) {
        setStudents(response.data.data);
        setFilteredStudents(response.data.data);
      } else {
        message.error(response?.data?.message || "Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      message.error(error?.response?.data?.message || "Error fetching students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Filter students based on search term
  useEffect(() => {
    const filtered = students.filter((student) => {
      return (
        student.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.parentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.phone?.includes(searchTerm)
      );
    });
    setFilteredStudents(filtered);
    setPage(0); // Reset to first page when search changes
  }, [searchTerm, students]);

  // Toggle student active status
  const toggleStudentStatus = async (id, currentStatus, studentName) => {
    const newStatus = !currentStatus;
    const action = newStatus ? "activate" : "deactivate";
    
    Modal.confirm({
      title: `Confirm ${action} Student`,
      content: `Are you sure you want to ${action} student "${studentName}"?`,
      okText: `Yes, ${action}`,
      cancelText: "No, Cancel",
      onOk: async () => {
        try {
          const response = await axios.patch(`/api/v1/student/toggle/${id}`);
          if (response?.data?.success) {
            message.success(response.data.message || `Student ${action}d successfully`);
            fetchStudents(); // Refresh the list
          } else {
            message.error(response?.data?.message || `Failed to ${action} student`);
          }
        } catch (error) {
          console.error("Error toggling student status:", error);
          message.error(error?.response?.data?.message || `Error toggling student status`);
        }
      },
    });
  };

  // Delete student
  const deleteStudent = async (id, studentName) => {
    Modal.confirm({
      title: "Are you sure you want to delete this student?",
      content: `Student "${studentName}" will be permanently deleted. This action cannot be undone.`,
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await axios.delete(`/api/v1/student/${id}`);
          if (response?.data?.success) {
            message.success(response.data.message || "Student deleted successfully");
            fetchStudents(); // Refresh the list
          } else {
            message.error(response?.data?.message || "Failed to delete student");
          }
        } catch (error) {
          console.error("Error deleting student:", error);
          message.error(error?.response?.data?.message || "Error deleting student");
        }
      },
    });
  };

  // Open edit modal
  const handleEditClick = (student) => {
    setSelectedStudent(student);
    setEditFormData({
      studentName: student.studentName || "",
      class: student.class || "",
      dob: student.dob ? moment(student.dob).format("YYYY-MM-DD") : "",
      parentName: student.parentName || "",
      phone: student.phone || "",
      email: student.email || "",
      address: student.address || "",
    });
    setEditModalOpen(true);
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update student
  const handleUpdateStudent = async () => {
    if (!selectedStudent) return;

    // Validation
    if (!editFormData.studentName.trim()) {
      message.error("Student name is required");
      return;
    }
    if (!editFormData.email.trim()) {
      message.error("Email is required");
      return;
    }
    if (!editFormData.class.trim()) {
      message.error("Class is required");
      return;
    }

    try {
      const response = await axios.put(`/api/v1/student/${selectedStudent._id}`, editFormData);
      if (response?.data?.success) {
        message.success(response.data.message || "Student updated successfully");
        setEditModalOpen(false);
        setSelectedStudent(null);
        fetchStudents(); // Refresh the list
      } else {
        message.error(response?.data?.message || "Failed to update student");
      }
    } catch (error) {
      console.error("Error updating student:", error);
      message.error(error?.response?.data?.message || "Error updating student");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    if (filteredStudents.length === 0) {
      message.warning("No students to export");
      return;
    }

    Modal.confirm({
      title: "Export to PDF",
      content: "Do you want to download the student list as a PDF?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(18);
        doc.setTextColor(33, 33, 33);
        doc.text("Guest Students List", 105, 16, { align: "center" });
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on: ${moment().format("DD/MM/YYYY HH:mm:ss")}`, 105, 24, { align: "center" });
        doc.text(`Total Students: ${filteredStudents.length}`, 105, 32, { align: "center" });
        
        // Define columns
        const columns = [
          "#",
          "Student Name",
          "Email",
          "Class",
          "Parent Name",
          "Phone",
          "Status",
          "Registered Date",
        ];
        
        // Define rows
        const rows = filteredStudents.map((student, index) => [
          index + 1,
          student.studentName || "N/A",
          student.email || "N/A",
          student.class || "N/A",
          student.parentName || "N/A",
          student.phone || "N/A",
          student.isActive ? "Active" : "Inactive",
          student.createdAt ? moment(student.createdAt).format("DD/MM/YYYY") : "N/A",
        ]);
        
        // Add table
        autoTable(doc, {
          head: [columns],
          body: rows,
          startY: 40,
          theme: "striped",
          headStyles: { fillColor: [25, 118, 210], textColor: 255 },
          styles: { fontSize: 9, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 30 },
            2: { cellWidth: 40 },
            3: { cellWidth: 15 },
            4: { cellWidth: 30 },
            5: { cellWidth: 20 },
            6: { cellWidth: 15 },
            7: { cellWidth: 25 },
          },
        });
        
        // Save PDF
        doc.save("Guest_Students_List.pdf");
      },
    });
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  return (
    <div>
      <PageTitle
        title="Guest Students - Admin Panel | Shree Mangalam School"
        description="Browse and manage the complete list of students enrolled at Shree Mangalam School through the Admin Panel. Search, filter, and access student profiles and course details easily."
        url="/admin/guest/students"
        keywords="guest student list admin, Shree Manglam students, school student directory, manage student data, view student records, admin panel students"
      />
      <Box>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{
            mb: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#2C3E50",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Guest Students
        </Typography>

        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: 3,
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/dashboard"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Dashboard
          </MUILink>
          <MUILink
            component={Link}
            to="/admin/reports"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Reports
          </MUILink>
          <Typography sx={{ color: "primary.main" }}>
            Guest Students
          </Typography>
        </Breadcrumbs>

        {/* Search and Export Section */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Name, Email, Parent Name or Phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#1976d2" }} />
                </IconButton>
              ),
            }}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={exportToPDF}
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            Export to PDF
          </Button>
        </Box>

        {/* Students Table */}
        <TableContainer component={Paper} sx={{ bgcolor: "white", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
          <Table sx={{ minWidth: 650 }} aria-label="students table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgb(25, 118, 210)" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                 <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Student Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Class</TableCell>
                 <TableCell sx={{ color: "white", fontWeight: "bold" }}>DOB</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Parent Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Phone</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Registered Date</TableCell>
               
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography sx={{ py: 4 }}>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography sx={{ py: 4 }}>No students found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentPageData().map((student, index) => (
                  <TableRow key={student._id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                       <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(student)}
                          sx={{ color: "#1976d2" }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteStudent(student._id, student.studentName)}
                          sx={{ color: "red" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                     <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={student.isActive === true}
                            onChange={() => toggleStudentStatus(student._id, student.isActive, student.studentName)}
                            color="success"
                            size="small"
                          />
                        }
                        label={student.isActive ? "Active" : "Inactive"}
                        labelPlacement="start"
                        sx={{ mr: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {student.studentName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>{student.email || "N/A"}</TableCell>
                    <TableCell>
                      <Chip label={student.class || "N/A"} size="small" color="primary" variant="outlined" />
                    </TableCell>
                     <TableCell>{student.dob ? moment(student.dob).format("DD/MM/YYYY") : "N/A"}</TableCell>
                    <TableCell>{student.parentName || "N/A"}</TableCell>
                    <TableCell>{student.phone || "N/A"}</TableCell>
                    <TableCell>
                      {student.createdAt ? moment(student.createdAt).format("DD/MM/YYYY") : "N/A"}
                    </TableCell>
                   
                 
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {filteredStudents.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredStudents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                borderTop: "1px solid #e0e0e0",
                "& .MuiTablePagination-displayedRows": {
                  color: "black",
                },
                "& .MuiTablePagination-selectLabel": {
                  color: "black",
                },
              }}
            />
          )}
        </TableContainer>

        {/* Edit Student Modal */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ backgroundColor: "#1976d2", color: "white" }}>
            Edit Student Information
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} mt={1}>
                <TextField
                  fullWidth
                  label="Student Name"
                  name="studentName"
                  value={editFormData.studentName}
                  onChange={handleEditInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6} mt={1}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Class"
                  name="class"
                  value={editFormData.class}
                  onChange={handleEditInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={editFormData.dob}
                  onChange={handleEditInputChange}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Parent Name"
                  name="parentName"
                  value={editFormData.parentName}
                  onChange={handleEditInputChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={editFormData.address}
                  onChange={handleEditInputChange}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setEditModalOpen(false)} variant="outlined" color="secondary">
              Cancel
            </Button>
            <Button onClick={handleUpdateStudent} variant="contained" color="primary">
              Update Student
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default AllStudentList;